// The tiles of a meeting fill their pane: of the numbers of columns, the one whose tiles are the largest
// — a few people large, many small — each from 3:4 to 16:9 (of a cell wider or taller than that, a tile
// takes as much as it can), the last row in the middle. No tile is smaller than the least; when no
// number of columns has tiles that large, they go as many to a row as fit, 4:3, in rows that scroll.

const NARROWEST = 3 / 4;
const WIDEST = 16 / 9;
export const TILE_GAP = 10;
const LEAST_TILE: [number, number] = [200, 140];
const LEAST_PHONE_TILE: [number, number] = [150, 110];
/** Tiles smaller than this (width, height) show the host's buttons only under the pointer or once
 * touched. */
const ROOMY_TILE: [number, number] = [240, 200];

/** The grid: columns, and each tile's width and height (whole pixels); whether the tiles are small;
 * whether its rows scroll (tiles of the least size do not all fit). */
export type Grid = { cols: number; w: number; h: number; compact: boolean; scroll: boolean };

/** The grid of `n` tiles in a pane of `w` × `h` (without its padding); `phone`: a phone's least size. */
export function layout(n: number, w: number, h: number, phone: boolean): Grid {
  const [leastW, leastH] = phone ? LEAST_PHONE_TILE : LEAST_TILE;
  let best: { cols: number; w: number; h: number } | null = null;
  let scroll = false;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const cw = (w - TILE_GAP * (cols - 1)) / cols;
    const ch = (h - TILE_GAP * (rows - 1)) / rows;
    const tw = Math.min(cw, ch * WIDEST);
    const th = Math.min(ch, tw / NARROWEST);
    if (tw >= leastW && th >= leastH && (best === null || tw * th > best.w * best.h)) best = { cols, w: tw, h: th };
  }
  if (best === null) {
    scroll = true;
    const cols = Math.max(1, Math.floor((w + TILE_GAP) / (leastW + TILE_GAP)));
    const tw = (w - TILE_GAP * (cols - 1)) / cols;
    best = { cols, w: tw, h: (tw * 3) / 4 };
  }
  // Whole pixels, rounded down: the grid never comes out a pixel larger than its pane, which would
  // scroll.
  const tw = Math.floor(best.w);
  const th = Math.floor(best.h);
  return { cols: best.cols, w: tw, h: th, compact: tw < ROOMY_TILE[0] || th < ROOMY_TILE[1], scroll };
}
